import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { UsageMeter } from "@/components/UsageMeter";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/context/UIContext";
import { Moon, Sun, Crown, Check, Loader2, Zap, Sparkles, Gem, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const TIER_META = {
  free: { name: "Free", icon: Zap, color: "#64748B" },
  pro:  { name: "Pro",  icon: Sparkles, color: "hsl(var(--primary))" },
  plus: { name: "Plus", icon: Gem, color: "#F59E0B" },
};

export default function Settings() {
  const { theme, setTheme } = useTheme();
  const { user, refreshUser } = useAuth();
  const { openPremium } = useUI();
  const [busy, setBusy] = useState(false);

  const planId = user?.plan || "free";
  const meta = TIER_META[planId] || TIER_META.free;
  const Icon = meta.icon;

  const cancel = async () => {
    setBusy(true);
    try {
      await api.post("/payments/cancel-subscription");
      await refreshUser?.();
      toast.success("Subscription cancelled — you're on Free.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not cancel.");
    } finally {
      setBusy(false);
    }
  };

  const ThemeOption = ({ value, icon: Ic, label }) => (
    <button
      onClick={() => setTheme(value)}
      data-testid={`theme-${value}`}
      className={`flex-1 rounded-xl border p-4 text-left transition-colors ${
        theme === value ? "border-primary bg-primary/5" : "border-border hover:border-border/80"
      }`}
    >
      <div className="flex items-center justify-between">
        <Ic className="h-5 w-5" />
        {theme === value && (
          <span className="h-5 w-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
            <Check className="h-3 w-3" />
          </span>
        )}
      </div>
      <p className="text-sm font-medium mt-3">{label}</p>
    </button>
  );

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <p className="overline text-muted-foreground">Account</p>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-1">Settings &amp; Plan</h1>
      </div>

      {/* Appearance */}
      <Card className="p-6">
        <h2 className="font-display text-lg font-semibold tracking-tight">Appearance</h2>
        <p className="text-sm text-muted-foreground mt-1 mb-4">Choose how VideoLens looks. Your choice is saved on this device.</p>
        <div className="flex gap-3">
          <ThemeOption value="light" icon={Sun} label="Light Mode" />
          <ThemeOption value="dark" icon={Moon} label="Dark Mode" />
        </div>
      </Card>

      {/* Profile */}
      <Card className="p-6">
        <h2 className="font-display text-lg font-semibold tracking-tight">Profile</h2>
        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input value={user?.name || ""} readOnly className="bg-secondary/40" />
          </div>
          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input value={user?.email || ""} readOnly className="bg-secondary/40" />
          </div>
        </div>
      </Card>

      {/* Subscription */}
      <Card className="p-6" data-testid="subscription-section">
        <h2 className="font-display text-lg font-semibold tracking-tight">Subscription</h2>
        <div className="mt-4 rounded-xl border border-border p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span
                className="flex h-8 w-8 items-center justify-center rounded-lg"
                style={{ background: `${meta.color}22`, color: meta.color }}
              >
                <Icon className="h-4 w-4" />
              </span>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold" style={{ color: meta.color }}>{meta.name}</span>
                  {user?.subscription_status === "active" && (
                    <Badge className="border-0 bg-emerald-500/15 text-emerald-500 text-[10px]">AUTOPAY ACTIVE</Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">{user?.analyses_limit} analyses / day</p>
              </div>
            </div>
            {user?.subscription_cycle && (
              <Badge variant="outline" className="text-[10px]">{user.subscription_cycle.toUpperCase()}</Badge>
            )}
          </div>

          <UsageMeter />

          <div className="mt-5 flex flex-wrap gap-2">
            {planId === "plus" ? (
              <Button variant="outline" onClick={cancel} disabled={busy} data-testid="cancel-subscription-btn" className="gap-1.5">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                <ShieldOff className="h-4 w-4" /> Cancel subscription
              </Button>
            ) : planId === "pro" ? (
              <>
                <Button onClick={openPremium} className="gap-2 bg-amber-500 hover:bg-amber-500/90 text-white" data-testid="settings-upgrade-plus-btn">
                  <Gem className="h-4 w-4" /> Upgrade to Plus
                </Button>
                <Button variant="outline" onClick={cancel} disabled={busy} data-testid="cancel-subscription-btn" className="gap-1.5">
                  <ShieldOff className="h-4 w-4" /> Cancel
                </Button>
              </>
            ) : (
              <Button onClick={openPremium} className="gap-2" data-testid="settings-upgrade-btn">
                <Crown className="h-4 w-4" /> Choose a plan
              </Button>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
